/**
 * DirectoryAddDoor: the one line every Directory picker ends its results
 * with once the search has answered a typed name, match or not (LP-26:
 * a name the Directory nearly knows, "Dr." matching a dozen, must still
 * be addable).
 *
 * Someone who can edit the Directory (the `direct_write` capability)
 * sees "Add to the Directory": it opens the Directory's suggest-a-resource
 * form pre-filled with the typed name, and saving it adds the listing in
 * one step (POST /suggested-resources/direct, gated on direct_write), so
 * the listing exists when the form closes. The
 * picker then takes the new listing the way it takes a search pick (a
 * box that lists only organizations takes only an organization).
 * Everyone else who may suggest sees "Request to add to the Directory":
 * the same form, sent for review; once it is sent the line reads
 * "Requested." for as long as the box holds the name that was requested,
 * and the picker keeps the typed text as its plain value.
 *
 * The host renders the door on every render, with `settled` true only
 * while its list is open on a finished search, so the form
 * outlives the list closing as the form takes the focus. The Button's
 * mousedown keeps the focus in the box, as an option's does. The form
 * draws in the overlay layer, but React events still travel up the
 * component tree, so the door stops them at the form's edge: a submit, a
 * click or a key inside the form never reaches the picker or the dialog
 * around it. Escape inside the form closes the form and nothing else.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useTenant } from '@/lib/auth';
import { canDirectWrite, canSuggest } from '@/lib/capabilities';
import { signedInName } from '@/lib/signedInName';
import type { SearchResult } from '@/lib/types';
import { Button } from '@/components/primitives/Button';
import { SuggestResourceForm } from '@/components/peace-hub-port/SuggestResourceForm';

export const DIRECTORY_ADD_LABEL = 'Add to the Directory';
export const DIRECTORY_REQUEST_LABEL = 'Request to add to the Directory';
export const DIRECTORY_REQUESTED = 'Requested.';

export interface DirectoryAddDoorProps {
  /** The text in the box as it stands. */
  query: string;
  /** The host's list is open on a finished search, match or not. */
  settled: boolean;
  /** The listing the form just added; the host takes it as its pick. */
  onAdded: (row: SearchResult) => void;
  /** A request was sent for this text; a host whose value is not the
   *  box's own text keeps it as its plain value here. */
  onRequested?: (text: string) => void;
  /** The kind of listing the form opens on: a box that lists only
   *  organizations opens on one, a box for a person's name on a person. */
  seedType?: 'organization' | 'individual';
  /** The one kind the box lists: a listing of the other kind, added
   *  after the form's kind was switched, stays in the Directory and the
   *  box keeps the typed text. */
  only?: 'organization';
  /** The door's data-testid; the Button and the requested line derive theirs. */
  testId: string;
}

type FormOpen = { add: boolean; name: string } | null;

/** True when this seat is offered the door at all (it may add to the
 *  Directory, or suggest); a host whose Tab moves to the door asks this. */
export function useDirectoryDoorSeat(): boolean {
  const { whoami } = useTenant();
  return canDirectWrite(whoami) || canSuggest(whoami);
}

const stop = (e: SyntheticEvent): void => e.stopPropagation();

export function DirectoryAddDoor({
  query,
  settled,
  onAdded,
  onRequested,
  seedType,
  only,
  testId,
}: DirectoryAddDoorProps): ReactNode {
  const { tenantSlug, whoami, principal } = useTenant();
  const mayAdd = canDirectWrite(whoami);
  const mayRequest = canSuggest(whoami);
  const [form, setForm] = useState<FormOpen>(null);
  const [requested, setRequested] = useState<string | null>(null);

  const q = query.trim();
  const showRequested = requested !== null && requested === q;
  const offer = settled && q !== '' && !showRequested && (mayAdd || mayRequest);

  return (
    <>
      {offer && (
        <div className="mt-1" data-testid={testId} onMouseDown={(e) => e.preventDefault()}>
          <Button
            variant="secondary"
            size="sm"
            data-testid={`${testId}-${mayAdd ? 'add' : 'request'}`}
            onClick={() => setForm({ add: mayAdd, name: q })}
          >
            {mayAdd ? DIRECTORY_ADD_LABEL : DIRECTORY_REQUEST_LABEL}
          </Button>
        </div>
      )}
      {showRequested && (
        <p role="status" data-testid={`${testId}-requested`} className="mt-1 text-xs text-ink-soft">
          {DIRECTORY_REQUESTED}
        </p>
      )}
      {form !== null && (
        <div
          className="contents"
          onSubmit={stop}
          onClick={stop}
          onMouseDown={stop}
          onMouseUp={stop}
          onPointerDown={stop}
          onFocus={stop}
          onBlur={stop}
          onChange={stop}
          onInput={stop}
          onKeyUp={stop}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') setForm(null);
          }}
        >
          <SuggestResourceForm
            isOpen
            onClose={() => setForm(null)}
            tenantSlug={tenantSlug}
            submitterDisplayName={signedInName(whoami, principal) || null}
            seedName={form.name}
            {...(seedType !== undefined ? { seedTargetType: seedType } : {})}
            {...(form.add
              ? {
                  onAdded: (row: SearchResult) => {
                    setForm(null);
                    if (only === undefined || row.type === only) onAdded(row);
                  },
                }
              : {
                  onSubmitted: () => {
                    setRequested(form.name);
                    onRequested?.(form.name);
                  },
                })}
          />
        </div>
      )}
    </>
  );
}
